/**
 * Embed-mode helpers.
 *
 * ehr-frontend is embedded inside va-portal as an iframe for certain users
 * (see va-portal's EhrSettingIframe.vue). va-portal signals the embed by
 * appending `?embed=true` to the iframe URL. The auth token is NOT in the URL;
 * it is fetched from the parent over postMessage (see embedAuth.js).
 *
 * The embed flag is only honored on routes that we've explicitly mapped
 * back to a va-portal tab — see EMBED_ROUTES below. On any other route the
 * app behaves normally even if the query param happens to be set.
 *
 * Each entry maps an ehr-frontend path to a va-portal "tab" name (the
 * `:tab` segment of va-portal's /setting/:tab route). When `vaTab` is
 * null, the tab is derived from whatever path segment follows the prefix
 * (passthrough). When `exact: true`, the mapping only matches when the
 * full path equals `ehrPath` (used for fixed mappings like
 * /my-account/account-info → tab "account").
 *
 * If you add a new entry here, mirror it in va-portal's EhrSettingIframe.vue
 * (the TAB_TO_EHR_PATH lookup) so both directions of the mapping agree.
 */

const EMBED_ROUTES = [
  // /my-practice/practice-setting[/:tab] ↔ va-portal /setting[/:tab].
  // All Personal Settings + Practice Settings tabs live here after the
  // /my-account → /my-practice merge. The /my-account/* paths still exist
  // as redirect-only entries in the router and never persist in the URL
  // long enough to need an embed scope entry of their own.
  { ehrPath: '/my-practice/practice-setting', vaTab: null, exact: false },
];

function matchEmbedRoute(path) {
  if (!path) return null;
  for (const route of EMBED_ROUTES) {
    if (route.exact) {
      if (path === route.ehrPath) return route;
    } else if (path === route.ehrPath || path.startsWith(`${route.ehrPath}/`)) {
      return route;
    }
  }
  return null;
}

/**
 * @param {{ path?: string, query?: Record<string, any> }} [route]
 *   Optional Vue Router route object. When omitted we fall back to
 *   window.location, which is necessary outside the component tree
 *   (e.g. inside service modules called during app init).
 * @returns {boolean}
 */
export function isEmbedMode(route) {
  let path;
  let embedParam;

  if (route && route.path != null) {
    path = route.path;
    embedParam = route.query && route.query.embed;
  } else if (typeof window !== 'undefined') {
    path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    embedParam = params.get('embed');
  } else {
    return false;
  }

  if (!matchEmbedRoute(path)) return false;
  return embedParam === 'true' || embedParam === true;
}

/**
 * Compute the va-portal tab name (the `:tab` segment of /setting/:tab) for
 * a given ehr-frontend path. Returns:
 *   - undefined if the path is not in the embed scope
 *   - '' (empty string) for the root of a passthrough mapping
 *   - the fixed `vaTab` value for an `exact` mapping
 *   - the segment after `ehrPath/` for a passthrough mapping
 */
export function vaTabForPath(path) {
  const match = matchEmbedRoute(path);
  if (!match) return undefined;
  if (match.vaTab !== null) return match.vaTab;
  const rest = path.slice(match.ehrPath.length).replace(/^\//, '');
  return rest;
}

export const EMBED_QUERY_KEY = 'embed';

// All ehr-frontend prefixes that participate in embed mode. Used by the
// router guard that re-attaches embed=true on cross-tab navigation.
export const EMBED_ROUTE_PREFIXES = EMBED_ROUTES.map((r) => r.ehrPath);

// Returns true if the path is within any embed route (regardless of the
// embed=true query param).
export function isEmbedScope(path) {
  return matchEmbedRoute(path) !== null;
}

// Back-compat: kept so existing imports don't break.
export const EMBED_PATH = '/my-practice/practice-setting';
